import type { Dispatch, ReactElement } from 'react';

import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import SomethingHappened from '@/components/SomethingHappened';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import type { CageWarning, WizardValidationError } from '../operateWizardHelpers';
import type { OperateWizardAction, OperateWizardState } from '../operateWizardReducer';
import ConfirmStep from '../steps/ConfirmStep';
import MergeDestinationStep from '../steps/merge/MergeDestinationStep';
import MergeSourcesStep from '../steps/merge/MergeSourcesStep';
import OperationKindStep from '../steps/OperationKindStep';
import RedistributionDestinationsStep from '../steps/redistribution/DestinationsStep';
import RedistributionSourcesStep from '../steps/redistribution/SourcesStep';
import BioscopeMovementsStep from '../steps/shared/BioscopeMovementsStep';
import SplitContinuationStep from '../steps/split/SplitContinuationStep';
import SplitTargetsStep from '../steps/split/SplitTargetsStep';
import SourceContinuationStep from '../steps/splitAndMerge/SourceContinuationStep';
import TargetStep from '../steps/splitAndMerge/TargetStep';

interface RenderStepArgs {
    state: OperateWizardState;
    dispatch: Dispatch<OperateWizardAction>;
    isLoading: boolean;
    hasError: boolean;
    locationCages: OverviewCage[];
    initialSource: { fishGroupId: number; name: string; currentCageId: number | null };
    validationErrors: WizardValidationError[];
    cageWarnings: CageWarning[];
    submitError: unknown;
    populationById: Map<number, FishGroupPopulationInfo>;
    operationMinDate: Date | null;
    t: (key: string, options?: Record<string, unknown>) => string;
}

export function renderStep(args: RenderStepArgs): ReactElement {
    const {
        state,
        dispatch,
        isLoading,
        hasError,
        locationCages,
        initialSource,
        validationErrors,
        cageWarnings,
        submitError,
        populationById,
        operationMinDate,
        t,
    } = args;

    if (isLoading) return <Spinner size="lg" />;
    if (hasError) return <SomethingHappened />;
    if (locationCages.length === 0 && state.step !== 'kind') {
        return <EmptyState title={t('actions.operate.noLocation')} appearance="quiet" />;
    }

    const emptyCages = locationCages.filter((c) => c.activeFishGroup === null);

    switch (state.step) {
        case 'kind':
            return (
                <OperationKindStep
                    locationCages={locationCages}
                    initialFishGroupId={state.initialSourceId}
                    currentKind={state.operationKind}
                    dispatch={dispatch}
                />
            );
        case 'split_continuation': {
            const sourceCageId = initialSource.currentCageId;
            const remainder = state.destinations.find((d) => d.cageId === sourceCageId) ?? null;
            return (
                <SplitContinuationStep
                    initialSource={initialSource}
                    locationCages={locationCages}
                    remainder={remainder}
                    errors={validationErrors}
                    sources={state.sources}
                    destinations={state.destinations}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        }
        case 'split_targets': {
            const sourceCageId = initialSource.currentCageId;
            const remainder = state.destinations.find((d) => d.cageId === sourceCageId) ?? null;
            const targets = state.destinations.filter((d) => d !== remainder);
            return (
                <SplitTargetsStep
                    initialSource={initialSource}
                    emptyCages={emptyCages}
                    remainder={remainder}
                    targets={targets}
                    errors={validationErrors}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        }
        case 'merge_sources':
            return (
                <MergeSourcesStep
                    locationCages={locationCages}
                    selectedSources={state.sources}
                    initialFishGroupId={state.initialSourceId}
                    populationById={populationById}
                    destinations={state.destinations}
                    dispatch={dispatch}
                />
            );
        case 'merge_destination':
            return (
                <MergeDestinationStep
                    locationCages={locationCages}
                    sources={state.sources}
                    destinations={state.destinations}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        case 'sam_continuation':
            return (
                <SourceContinuationStep
                    initialSource={initialSource}
                    locationCages={locationCages}
                    continuation={state.destinations[0] ?? null}
                    errors={validationErrors}
                    sources={state.sources}
                    destinations={state.destinations}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        case 'sam_target':
            return (
                <TargetStep
                    initialSource={initialSource}
                    locationCages={locationCages}
                    mergeTargetDestination={state.destinations[1] ?? null}
                    samTargetMode={state.samTargetMode}
                    sources={state.sources}
                    destinations={state.destinations}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        case 'redist_sources':
            return (
                <RedistributionSourcesStep
                    locationCages={locationCages}
                    selectedSources={state.sources}
                    initialFishGroupId={state.initialSourceId}
                    populationById={populationById}
                    destinations={state.destinations}
                    dispatch={dispatch}
                />
            );
        case 'redist_destinations':
            return (
                <RedistributionDestinationsStep
                    locationCages={locationCages}
                    sources={state.sources}
                    destinations={state.destinations}
                    errors={validationErrors}
                    populationById={populationById}
                    dispatch={dispatch}
                />
            );
        case 'bioscope_movements':
            return (
                <BioscopeMovementsStep
                    sources={state.sources}
                    destinations={state.destinations}
                    locationCages={locationCages}
                    bioscopeMovements={state.bioscopeMovements}
                    dispatch={dispatch}
                />
            );
        case 'confirm':
            return (
                <ConfirmStep
                    state={state}
                    locationCages={locationCages}
                    cageWarnings={cageWarnings}
                    submitError={submitError}
                    operationMinDate={operationMinDate}
                    dispatch={dispatch}
                />
            );
    }
}
